import React, { useState, useEffect, useMemo } from 'react';
import { Clock, Calendar, UserX, Check, Settings, ChevronDown, ChevronUp, Plus, X, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';
import { persistProfilePref } from '../../utils/persistProfilePref';

// Generate time slots from start time, interval, and count, with optional lunch break
const generateTimeSlots = (startTime, intervalMinutes, count, lunchStart = '12:00', lunchEnd = '13:00') => {
  const slots = [];
  const [hours, minutes] = startTime.split(':').map(Number);
  let currentMinutes = hours * 60 + minutes;
  const [lsH, lsM] = lunchStart.split(':').map(Number);
  const [leH, leM] = lunchEnd.split(':').map(Number);
  const lunchStartMin = lsH * 60 + lsM;
  const lunchEndMin = leH * 60 + leM;
  
  let generated = 0;
  while (generated < count) {
    if (currentMinutes >= lunchStartMin && currentMinutes < lunchEndMin) {
      currentMinutes = lunchEndMin;
      continue;
    }
    const h = Math.floor(currentMinutes / 60);
    const m = currentMinutes % 60;
    if (h >= 24) break;
    const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    slots.push({
      id: `slot-${generated}`,
      time: timeStr,
      display: formatTimeDisplay(timeStr),
      status: 'open',
    });
    generated++;
    currentMinutes += intervalMinutes;
  }
  return slots;
};

// Format time for display (e.g., "08:30" -> "8:30 AM")
const formatTimeDisplay = (time24) => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Generate numbered patient slots (#1, #2, ... #N)
const generateNumberedSlots = (count) => {
  const slots = [];
  for (let i = 1; i <= count; i++) {
    slots.push({
      id: `slot-${i - 1}`,
      time: `pt-${i}`,
      display: `#${i}`,
      status: 'open',
    });
  }
  return slots;
};

// Format today's short date (e.g., "25 Mar")
const todayShortDate = () => {
  const d = new Date();
  return `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' })}`;
};

const PatientLabelSection = ({
  patientLabel,
  setPatientLabel,
  clinicSchedule,
  setClinicSchedule,
  onMarkNoShow,
  onMarkDone,
  compact = false,
  notes = '',
  chiefComplaint = ''
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showScheduleSetup, setShowScheduleSetup] = useState(false);
  const [customLabel, setCustomLabel] = useState('');

  // Auto-detect critical findings from notes for label suggestions.
  // Feb 27, 2026 — debounce-state the regex pipeline (was running on EVERY
  // keystroke as a useMemo dependency, contributing to typing lag). Runs
  // 400ms after the provider pauses.
  const [debouncedNotes, setDebouncedNotes] = useState(notes);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedNotes(notes), 400);
    return () => clearTimeout(t);
  }, [notes]);
  const suggestedLabels = useMemo(() => {
    if (!debouncedNotes || debouncedNotes.length < 20) return [];
    const suggestions = [];
    const text = debouncedNotes.toLowerCase();
    // Abnormal vitals
    const bpMatch = debouncedNotes.match(/(?:BP|blood pressure)[:\s]*(\d{2,3})\s*[\/]\s*(\d{2,3})/i);
    if (bpMatch) {
      const sys = parseInt(bpMatch[1]), dia = parseInt(bpMatch[2]);
      if (sys >= 140 || dia >= 90) suggestions.push(`BP ${bpMatch[1]}/${bpMatch[2]}`);
    }
    // A1c
    const a1cMatch = debouncedNotes.match(/(?:A1[cC]|HbA1c|hemoglobin a1c)[:\s]*(\d+\.?\d*)/i);
    if (a1cMatch && parseFloat(a1cMatch[1]) >= 6.5) suggestions.push(`A1c ${a1cMatch[1]}`);
    // BMI
    const bmiMatch = debouncedNotes.match(/(?:BMI)[:\s]*(\d+\.?\d*)/i);
    if (bmiMatch && parseFloat(bmiMatch[1]) >= 30) suggestions.push(`BMI ${bmiMatch[1]}`);
    // SI/HI safety
    if (text.includes('suicid') || text.includes('homicid') || text.includes('self-harm') || text.includes('self harm')) {
      suggestions.push('SI/HI Screen');
    }
    // PHQ-9
    const phqMatch = debouncedNotes.match(/PHQ[\s-]*9[:\s]*(\d+)/i);
    if (phqMatch) suggestions.push(`PHQ-9: ${phqMatch[1]}`);
    // Abnormal labs
    const labPatterns = [
      { re: /(?:WBC|white blood)[:\s]*(\d+\.?\d*)/i, label: 'WBC', high: 11, low: 4 },
      { re: /(?:Hgb|hemoglobin)[:\s]*(\d+\.?\d*)/i, label: 'Hgb', high: 999, low: 12 },
      { re: /(?:TSH)[:\s]*(\d+\.?\d*)/i, label: 'TSH', high: 4.5, low: 0.4 },
      { re: /(?:creatinine|Cr)[:\s]*(\d+\.?\d*)/i, label: 'Cr', high: 1.2, low: 0 },
      { re: /(?:GFR|eGFR)[:\s]*(\d+)/i, label: 'GFR', high: 999, low: 60 },
    ];
    for (const lp of labPatterns) {
      const m = debouncedNotes.match(lp.re);
      if (m) {
        const val = parseFloat(m[1]);
        if (val > lp.high || val < lp.low) suggestions.push(`${lp.label} ${m[1]}`);
      }
    }
    // Chief complaint as fallback
    if (suggestions.length === 0 && chiefComplaint && chiefComplaint.length > 2) {
      suggestions.push(chiefComplaint.substring(0, 30));
    }
    return suggestions.slice(0, 4);
  }, [debouncedNotes, chiefComplaint]);
  
  // Schedule setup defaults — matches FPPE 14-16 pts/day, afternoon starts 1:10 PM
  const [scheduleStart, setScheduleStart] = useState('08:30');
  const [scheduleInterval, setScheduleInterval] = useState('20');
  const [scheduleCount, setScheduleCount] = useState('16');
  const [lunchStart, setLunchStart] = useState('12:00');
  const [lunchEnd, setLunchEnd] = useState('13:10');
  const [scheduleMode, setScheduleMode] = useState(() => {
    return localStorage.getItem('writeright_schedule_mode') || 'numbered';
  });
  
  // Stats
  const doneCount = clinicSchedule?.filter(s => s.status === 'done').length || 0;
  const noshowCount = clinicSchedule?.filter(s => s.status === 'noshow').length || 0;
  const remainingCount = clinicSchedule?.filter(s => s.status === 'open' || s.status === 'current').length || 0;

  // Handle slot click — selects as label AND marks previous as done
  const handleSlotClick = (slot) => {
    if (slot.status === 'noshow') {
      toast.error('This slot is marked No Show');
      return;
    }
    
    // Toggle: if already done or current, revert to open
    if (slot.status === 'done' || slot.status === 'current') {
      const updatedSchedule = clinicSchedule.map(s => 
        s.id === slot.id ? { ...s, status: 'open' } : s
      );
      setClinicSchedule(updatedSchedule);
      localStorage.setItem('clinicSchedule', JSON.stringify(updatedSchedule));
      if (patientLabel?.includes(slot.display)) setPatientLabel('');
      toast.success(`${slot.display} — unchecked`);
      return;
    }
    
    // Build label: include current time for numbered mode so signature block gets a timestamp
    const isNumbered = slot.time?.startsWith('pt-');
    if (isNumbered) {
      const now = new Date();
      const h = now.getHours() % 12 || 12;
      const m = now.getMinutes().toString().padStart(2, '0');
      const period = now.getHours() >= 12 ? 'PM' : 'AM';
      setPatientLabel(`${todayShortDate()} ${slot.display} ${h}:${m} ${period}`);
    } else {
      setPatientLabel(`${todayShortDate()} ${slot.display}`);
    }
    
    // Mark this slot as current, mark any previous 'current' as 'done'
    const updatedSchedule = clinicSchedule.map(s => {
      if (s.id === slot.id) return { ...s, status: 'current' };
      if (s.status === 'current') return { ...s, status: 'done' }; // auto-done previous
      return s;
    });
    setClinicSchedule(updatedSchedule);
    localStorage.setItem('clinicSchedule', JSON.stringify(updatedSchedule));
    
    if (onMarkDone) onMarkDone(slot);
  };
  
  // No-show toggle (right-click or long-press via the X overlay)
  const handleNoShowToggle = (slot, e) => {
    e.stopPropagation();
    const newStatus = slot.status === 'noshow' ? 'open' : 'noshow';
    const updatedSchedule = clinicSchedule.map(s => ({
      ...s,
      status: s.id === slot.id ? newStatus : s.status
    }));
    setClinicSchedule(updatedSchedule);
    localStorage.setItem('clinicSchedule', JSON.stringify(updatedSchedule));
    
    if (newStatus === 'noshow') {
      toast.info(`${slot.display} — No Show`);
      if (patientLabel?.includes(slot.display)) setPatientLabel('');
      if (onMarkNoShow) onMarkNoShow(slot);
    }
  };
  
  // Generate schedule
  const handleGenerateSchedule = () => {
    let slots;
    if (scheduleMode === 'numbered') {
      slots = generateNumberedSlots(parseInt(scheduleCount));
    } else {
      slots = generateTimeSlots(
        scheduleStart, parseInt(scheduleInterval), parseInt(scheduleCount), lunchStart, lunchEnd
      );
    }
    setClinicSchedule(slots);
    setShowScheduleSetup(false);
    toast.success(`Generated ${slots.length} ${scheduleMode === 'numbered' ? 'patient slots' : 'time slots'}`);
    localStorage.setItem('clinicSchedule', JSON.stringify(slots));
    localStorage.setItem('clinicScheduleDate', new Date().toDateString());
    localStorage.setItem('writeright_schedule_mode', scheduleMode);
    persistProfilePref({ schedule_mode: scheduleMode });
  };
  
  // Load or auto-generate schedule
  useEffect(() => {
    const savedDate = localStorage.getItem('clinicScheduleDate');
    const today = new Date().toDateString();
    if (savedDate === today) {
      const saved = localStorage.getItem('clinicSchedule');
      if (saved) {
        try { setClinicSchedule(JSON.parse(saved)); return; } catch (e) { /* ignore */ }
      }
    } else {
      localStorage.removeItem('clinicSchedule');
      localStorage.removeItem('clinicScheduleDate');
    }
    // Auto-generate default
    const defaultSlots = generateTimeSlots('08:30', 20, 16, '12:00', '13:10');
    setClinicSchedule(defaultSlots);
    localStorage.setItem('clinicSchedule', JSON.stringify(defaultSlots));
    localStorage.setItem('clinicScheduleDate', today);
  }, [setClinicSchedule]);
  
  const handleCustomLabelSubmit = () => {
    if (customLabel.trim()) { setPatientLabel(customLabel.trim()); setCustomLabel(''); }
  };

  const handleSetNow = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = Math.round(now.getMinutes() / 5) * 5;
    const time24 = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    setPatientLabel(`${todayShortDate()} ${formatTimeDisplay(time24)}`);
  };

  // Slot style
  const getSlotClass = (slot) => {
    switch (slot.status) {
      case 'current': return 'bg-primary text-primary-foreground ring-1 ring-primary font-bold';
      case 'done': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40';
      case 'noshow': return 'bg-red-500/10 text-red-400/60 border-red-500/30 line-through';
      default: return 'bg-card hover:bg-primary/10 text-foreground border-border';
    }
  };

  return (
    <div className="space-y-1.5" data-testid="daily-schedule-section">
      {/* Header — always visible */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex items-center gap-2 w-full text-left group"
        data-testid="daily-schedule-toggle"
      >
        <span className="text-[11px] text-muted-foreground flex items-center gap-1 cursor-pointer group-hover:text-foreground transition-colors">
          <Calendar className="w-3 h-3" />
          Daily Schedule
        </span>
        {patientLabel && (
          <span className="px-2 py-0.5 rounded-full bg-primary/20 text-primary text-[11px] font-medium">
            {patientLabel}
          </span>
        )}
        {!patientLabel && <span className="text-[10px] text-muted-foreground italic">No label set</span>}
        {/* Inline stats */}
        {clinicSchedule?.length > 0 && (
          <span className="text-[9px] text-muted-foreground ml-auto mr-1 flex items-center gap-2">
            {doneCount > 0 && <span className="text-emerald-400">{doneCount} done</span>}
            {noshowCount > 0 && <span className="text-red-400">{noshowCount} NS</span>}
            <span>{remainingCount} left</span>
          </span>
        )}
        {isCollapsed ? <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" /> : <ChevronUp className="w-3 h-3 text-muted-foreground shrink-0" />}
      </button>

      {/* Expanded content — merged schedule strip */}
      {!isCollapsed && (
        <div className="space-y-2">
          {/* Unified time slot grid — tap to select & auto-mark done */}
          {clinicSchedule && clinicSchedule.length > 0 && (
            <div className="flex flex-wrap gap-1" data-testid="schedule-grid">
              {clinicSchedule.map(slot => (
                <div key={slot.id} className="relative group">
                  <button
                    onClick={() => handleSlotClick(slot)}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-all border flex items-center gap-0.5 ${getSlotClass(slot)}`}
                    data-testid={`slot-${slot.time}`}
                    title={slot.status === 'done' ? 'Done' : slot.status === 'noshow' ? 'No Show' : 'Tap to select'}
                  >
                    {slot.status === 'done' && <Check className="w-2.5 h-2.5" />}
                    {slot.status === 'noshow' && <UserX className="w-2.5 h-2.5" />}
                    {slot.display}
                  </button>
                  {/* No-show toggle overlay */}
                  {slot.status !== 'current' && (
                    <button
                      onClick={(e) => handleNoShowToggle(slot, e)}
                      className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px]
                        opacity-0 group-hover:opacity-100 transition-opacity
                        ${slot.status === 'noshow' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}
                      title={slot.status === 'noshow' ? 'Undo No Show' : 'Mark No Show'}
                    >
                      {slot.status === 'noshow' ? <RotateCcw className="w-2 h-2" /> : <X className="w-2 h-2" />}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Quick actions row — Now, Custom label, Settings */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleSetNow} className="h-6 text-[10px] px-2" data-testid="schedule-now-btn">
              <Clock className="w-2.5 h-2.5 mr-0.5" />Now
            </Button>
            <div className="flex items-center gap-1 flex-1 min-w-[180px]">
              <Input
                type="text"
                placeholder="Custom label (e.g., 'Walk-in #3')"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCustomLabelSubmit()}
                className="h-6 text-[10px] flex-1"
                data-testid="custom-label-input"
              />
              <Button variant="secondary" size="sm" onClick={handleCustomLabelSubmit} disabled={!customLabel.trim()} className="h-6 w-6 p-0" data-testid="custom-label-submit">
                <Plus className="w-3 h-3" />
              </Button>
            </div>
            {/* Auto-detected label suggestions */}
            {suggestedLabels.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-[9px] text-muted-foreground/60">Quick:</span>
                {suggestedLabels.map((s, si) => (
                  <button
                    key={si}
                    onClick={() => {
                      const current = patientLabel || '';
                      const sep = current && !current.endsWith(' ') ? ' | ' : '';
                      setPatientLabel(current + sep + s);
                    }}
                    className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 transition-colors"
                    data-testid={`label-suggestion-${si}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={() => setShowScheduleSetup(true)} className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground" title="Configure schedule" data-testid="schedule-settings-btn">
              <Settings className="w-3 h-3" />
            </Button>
            {patientLabel && (
              <Button variant="ghost" size="sm" onClick={() => setPatientLabel('')} className="h-6 w-6 p-0 text-muted-foreground hover:text-red-400" title="Clear label" data-testid="clear-label-btn">
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
      )}
      
      {/* Schedule Setup Modal */}
      {showScheduleSetup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-sm">
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground text-sm">Configure Daily Schedule</h3>
                <button onClick={() => setShowScheduleSetup(false)} className="p-1 hover:bg-secondary rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>
              {/* Mode toggle: Time-based vs Numbered */}
              <div className="flex gap-1 bg-secondary rounded-lg p-1" data-testid="schedule-mode-toggle">
                <button
                  onClick={() => setScheduleMode('time')}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                    scheduleMode === 'time' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="schedule-mode-time"
                >Time Slots (8:30, 8:50...)</button>
                <button
                  onClick={() => setScheduleMode('numbered')}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                    scheduleMode === 'numbered' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="schedule-mode-numbered"
                >Numbered (#1, #2, #3...)</button>
              </div>
              {scheduleMode === 'time' ? (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Start Time</Label>
                      <Input type="time" value={scheduleStart} onChange={(e) => setScheduleStart(e.target.value)} className="h-9" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Interval</Label>
                      <Select value={scheduleInterval} onValueChange={setScheduleInterval}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 min</SelectItem>
                          <SelectItem value="20">20 min</SelectItem>
                          <SelectItem value="30">30 min</SelectItem>
                          <SelectItem value="45">45 min</SelectItem>
                          <SelectItem value="60">60 min</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Patients</Label>
                      <Input type="number" min="1" max="30" value={scheduleCount} onChange={(e) => setScheduleCount(e.target.value)} className="h-9" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Lunch Start</Label>
                      <Input type="time" value={lunchStart} onChange={(e) => setLunchStart(e.target.value)} className="h-9" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Lunch End</Label>
                      <Input type="time" value={lunchEnd} onChange={(e) => setLunchEnd(e.target.value)} className="h-9" />
                    </div>
                  </div>
                  <div className="p-2 rounded bg-secondary/50 text-xs text-muted-foreground">
                    Preview: {formatTimeDisplay(scheduleStart)} to {
                      formatTimeDisplay((() => {
                        const [h, m] = scheduleStart.split(':').map(Number);
                        const totalMin = h * 60 + m + (parseInt(scheduleInterval) * (parseInt(scheduleCount) - 1));
                        return `${Math.floor(totalMin / 60).toString().padStart(2, '0')}:${(totalMin % 60).toString().padStart(2, '0')}`;
                      })())
                    } ({scheduleCount} slots, {scheduleInterval}min intervals)
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Number of Patients</Label>
                    <Input type="number" min="1" max="30" value={scheduleCount} onChange={(e) => setScheduleCount(e.target.value)} className="h-9" />
                  </div>
                  <div className="p-2 rounded bg-secondary/50 text-xs text-muted-foreground">
                    Preview: Patient #1 through #{scheduleCount} — tap to track progress
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowScheduleSetup(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleGenerateSchedule} className="flex-1">Generate</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PatientLabelSection;
